/**
 * @fileoverview Shared validation for QA agent rulesets — the single source of
 * truth for "does this agent have enough valid rulesets to submit". The wizard
 * progress bar (per-scope step completion) and the AgentDetailsForm submit gate
 * build on the same per-scope predicates so the two never disagree.
 * @owner @Appboy/agent-console
 */

import { isStepRulesetScope } from "./isStepRulesetScope";
import type { InteractionSite } from "./types";
import { INTERACTION_SITES, RULESET_SCOPES } from "./types";

/**
 * Minimal structural shape of a form ruleset. Kept local (rather than importing
 * AgentRuleset / CustomAgentFormValues) so `lib/agentConsole` stays free of a
 * dependency on the CustomAgentForm component. Fields are optional because form
 * state can hold partially-entered rulesets.
 */
interface RulesetLike {
  name?: string;
  rules?: (string | undefined)[];
  scope?: string;
}

/** A ruleset counts toward completion when it has a name and at least one non-blank rule. */
export const isValidRuleset = (ruleset: RulesetLike | undefined): boolean =>
  (ruleset?.name?.trim() ?? "").length > 0 &&
  Array.isArray(ruleset?.rules) &&
  ruleset.rules.some((rule) => (rule?.trim() ?? "").length > 0);

/**
 * True when any ruleset is a valid `canvas`-scope ruleset. The completion rule
 * for the wizard's "Canvas configuration" step.
 */
export const hasValidCanvasRuleset = (rulesets: (RulesetLike | undefined)[]): boolean =>
  rulesets.some((ruleset) => ruleset?.scope === RULESET_SCOPES.CANVAS && isValidRuleset(ruleset));

/**
 * True when any ruleset is a valid step-scoped ruleset (message_step or
 * audience_paths_step). No individual step scope is required on its own: an
 * agent with only Audience Paths step rulesets satisfies it. The completion
 * rule for the wizard's "Canvas steps" step.
 */
export const hasValidStepRuleset = (rulesets: (RulesetLike | undefined)[]): boolean =>
  rulesets.some((ruleset) => isStepRulesetScope(ruleset?.scope) && isValidRuleset(ruleset));

/**
 * Returns true when the agent's rulesets are sufficient to submit for the given
 * interaction site:
 * - Canvas QA: hasValidCanvasRuleset OR hasValidStepRuleset. Neither scope is
 *   required on its own: execution skips a scope with no rulesets, so a
 *   configuration-only or steps-only Standard is valid. The wizard uses the same
 *   two predicates for per-step completion, so the two cannot drift.
 * - Other QA sites (Campaign QA): at least one valid ruleset (scope is undefined).
 */
export const hasValidRulesetsForSite = (
  rulesets: (RulesetLike | undefined)[] | undefined,
  interactionSite: InteractionSite | undefined
): boolean => {
  if (!Array.isArray(rulesets)) {
    return false;
  }

  if (interactionSite === INTERACTION_SITES.CANVAS_QA) {
    return hasValidCanvasRuleset(rulesets) || hasValidStepRuleset(rulesets);
  }

  return rulesets.some(isValidRuleset);
};
